import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Clock, 
  Send, 
  Facebook, 
  Instagram, 
  Youtube,
  Heart,
  Sparkles,
  ChevronDown,
  Copy,
  Check,
  ExternalLink,
  MessageSquare,
  MessageCircle,
  Building,
  User,
  Tag,
  ShieldCheck,
  ArrowRight
} from 'lucide-react';
import toast from 'react-hot-toast';
import HeroSection from '../components/common/HeroSection';
import { useCelebration } from '../contexts/CelebrationContext';
import api from '../config/api';

const INQUIRY_TOPICS = [
  'General Inquiry',
  'Sponsorship & Partnerships'
];

const ContactPage = () => {
  const { triggerConfetti } = useCelebration();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: 'General Inquiry',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [logoUrl, setLogoUrl] = useState('');
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  // Fetch logo from about page content
  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const response = await api.get('/about-page');
        if (response.ok) {
          const data = await response.json();
          setLogoUrl(data.logo || '');
        }
      } catch (error) {
        console.error('Error fetching logo:', error);
      }
    };
    fetchLogo();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCopyEmail = () => {
    navigator.clipboard.writeText('beatsofredmond@gmail.com');
    setCopiedEmail(true);
    toast.success('Email copied to clipboard!');
    setTimeout(() => setCopiedEmail(false), 2500);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim() || !formData.phone.trim() || !formData.message.trim()) {
      toast.error('Please fill in all required fields including your phone number.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address.');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/contact', formData);
      const data = await response.json();
      
      if (response.ok) {
        try {
          triggerConfetti();
        } catch (confettiErr) {
          console.debug('Confetti error:', confettiErr);
        }
        setShowSuccessModal(true);
        setFormData({ name: '', email: '', phone: '', subject: 'General Inquiry', message: '' });
      } else {
        toast.error(data.error || 'Failed to send message. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      title: "Email",
      value: "beatsofredmond@gmail.com",
      subtitle: "Online support & general inquiries",
      link: "mailto:beatsofredmond@gmail.com",
      actionLabel: "Send Email",
      canCopy: true,
      bgColor: "from-blue-500 to-blue-600",
      shadowColor: "shadow-blue-500/25",
      textColor: "text-blue-600 hover:text-blue-700",
      accentBorder: "group-hover:border-blue-200"
    },
    {
      icon: Phone,
      title: "Phone",
      value: "(206) 369-9576",
      subtitle: "Mon–Fri, 9AM–6PM PST",
      link: "tel:+12063699576",
      actionLabel: "Call Now",
      canCopy: false,
      bgColor: "from-emerald-500 to-green-600",
      shadowColor: "shadow-emerald-500/25",
      textColor: "text-emerald-600 hover:text-emerald-700",
      accentBorder: "group-hover:border-emerald-200"
    },
    {
      icon: MapPin,
      title: "Address",
      value: "9256 225th Way NE",
      subtitle: "Redmond, WA 98053",
      link: "https://www.google.com/maps/search/?api=1&query=9256+225th+Way+NE,+Redmond,+WA+98053",
      actionLabel: "Get Directions",
      canCopy: false,
      bgColor: "from-red-500 to-rose-600",
      shadowColor: "shadow-red-500/25",
      textColor: "text-rose-600 hover:text-rose-700",
      accentBorder: "group-hover:border-rose-200"
    },
    {
      icon: Clock,
      title: "Office Hours",
      value: "Mon–Fri: 9AM–6PM",
      subtitle: "Sat: 10AM–4PM • Sun: Closed",
      link: null,
      actionLabel: "Active Support Desk",
      canCopy: false,
      bgColor: "from-purple-500 to-indigo-600",
      shadowColor: "shadow-purple-500/25",
      textColor: "text-purple-600 hover:text-purple-700",
      accentBorder: "group-hover:border-purple-200"
    }
  ];

  const departments = [
    {
      name: "General Inquiries",
      email: "beatsofredmond@gmail.com",
      description: "For broad questions about community memberships, programs, and updates.",
      icon: MessageCircle
    },
    {
      name: "Sponsorship & Partnerships",
      email: "beatsofredmond@gmail.com",
      description: "For corporate matching, local business partnerships, and festival sponsorships.",
      icon: Heart
    },
    {
      name: "Media & PR Relations",
      email: "beatsofredmond@gmail.com",
      description: "For press releases, media kits, publication requests, and public relations.",
      icon: Building
    }
  ];

  const socialLinks = [
    { 
      name: 'Facebook', 
      handle: '@BeatsOfRedmond',
      href: 'https://www.facebook.com/BeatsOfRedmond/', 
      icon: Facebook,
      color: 'hover:bg-blue-600 hover:border-blue-600'
    },
    { 
      name: 'Instagram', 
      handle: '@beatsofwa',
      href: 'https://www.instagram.com/beatsofwa/', 
      icon: Instagram,
      color: 'hover:bg-pink-600 hover:border-pink-600'
    },
    { 
      name: 'YouTube', 
      handle: 'Beats of Washington',
      href: 'https://www.youtube.com/c/BeatsOfRedmond', 
      icon: Youtube,
      color: 'hover:bg-red-600 hover:border-red-600'
    }
  ];

  const faqs = [
    {
      question: "Are your events free and family-friendly?",
      answer: "Yes! Most of our cultural celebrations, Ganeshotsav processions, and festival performances are free and open to everyone in the community. Any special ticketed concerts are clearly marked on our Events page."
    },
    {
      question: "How can I support Beats of Washington through donations or sponsorships?",
      answer: "We are a registered 501(c)(3) non-profit organization. Donations are tax-deductible to the extent allowed by law. Visit our Donate page for online giving or contact us for corporate matching and sponsorship opportunities."
    },
    {
      question: "Where do regular rehearsals and workshops take place?",
      answer: "Our practice sessions and workshops take place in Redmond, WA and nearby Eastside community spaces. Schedule details and practice locations are shared with active members and registrants."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Contact Us - Beats of Washington</title>
        <meta 
          name="description" 
          content="Get in touch with Beats of Washington. Contact us about community events, sponsorships, partnerships, or general inquiries." 
        />
      </Helmet>

      {/* Hero Section — Styled to match the rich Donation & Brand aesthetics */}
      <HeroSection
        title={
          <>
            <span>Let's Start a</span>
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 via-orange-400 to-red-400">
              Conversation
            </span>
          </>
        }
        description="Whether you have questions about our upcoming events, community partnerships, or supporting our mission, our team is excited to connect with you."
        badge="✦ WE'D LOVE TO CONNECT"
        logoUrl={logoUrl}
        showLogo={true}
        className="bg-gradient-to-br from-slate-900 via-purple-950 to-orange-950"
        floatingElements={[
          { icon: Mail, position: 'top-12 left-12', animation: 'animate-pulse text-amber-400/20' },
          { icon: Sparkles, position: 'top-20 right-28', animation: 'animate-spin-slow text-yellow-300/20' },
          { icon: Phone, position: 'bottom-20 left-1/4', animation: 'animate-bounce text-orange-400/20' },
          { icon: Heart, position: 'bottom-12 right-20', animation: 'animate-pulse text-red-400/20' }
        ]}
        interactiveElements={[
          { icon: MessageCircle, label: 'Message', color: 'text-teal-300' },
          { icon: Phone, label: 'Call', color: 'text-emerald-300' },
          { icon: Mail, label: 'Email', color: 'text-sky-300' },
          { icon: Clock, label: 'Hours', color: 'text-purple-300' },
          { icon: Heart, label: 'Support', color: 'text-rose-300' }
        ]}
      />

      {/* Section 1: Quick Contact Cards */}
      <section className="py-12 sm:py-16 bg-gradient-to-b from-gray-50 via-slate-50 to-white relative">
        <div className="container-custom px-4 sm:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 -mt-8 sm:-mt-12 relative z-20">
            {contactInfo.map((info, idx) => {
              const Icon = info.icon;
              return (
                <div
                  key={idx}
                  className={`bg-white/95 backdrop-blur-xl rounded-2xl p-5 sm:p-6 border border-gray-100 ${info.accentBorder} shadow-xl hover:shadow-2xl hover:-translate-y-1.5 transition-all duration-300 flex flex-col justify-between group`}
                >
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${info.bgColor} text-white flex items-center justify-center shadow-lg ${info.shadowColor} transform transition-all duration-300 group-hover:scale-110 ${info.title === "Office Hours" ? "" : "group-hover:rotate-6"}`}>
                        <Icon className={`w-7 h-7 text-white ${info.title === "Office Hours" ? "group-hover:animate-spin" : ""}`} />
                      </div>
                      {info.canCopy && (
                        <button
                          type="button"
                          onClick={handleCopyEmail}
                          title="Copy email to clipboard"
                          className="p-2 rounded-xl bg-gray-100 hover:bg-blue-50 hover:text-blue-600 text-gray-500 transition-colors"
                          aria-label="Copy email"
                        >
                          {copiedEmail ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                        </button>
                      )}
                    </div>

                    <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">
                      {info.title}
                    </h3>
                    <p className="text-base sm:text-lg font-bold text-gray-900 break-words leading-snug">
                      {info.value}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {info.subtitle}
                    </p>
                  </div>

                  {info.link ? (
                    <a
                      href={info.link}
                      target={info.link.startsWith('http') ? '_blank' : '_self'}
                      rel={info.link.startsWith('http') ? 'noopener noreferrer' : ''}
                      className={`mt-4 pt-3 border-t border-gray-100 text-xs sm:text-sm font-semibold ${info.textColor} flex items-center gap-1.5 transition-colors`}
                    >
                      <span>{info.actionLabel}</span>
                      <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                    </a>
                  ) : (
                    <div className="mt-4 pt-3 border-t border-gray-100 text-xs text-purple-600 font-semibold flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-purple-500 animate-pulse"></span>
                      <span>{info.actionLabel}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section 2: Unified Form & Department Hub */}
      <section className="py-12 sm:py-20 bg-white relative overflow-hidden">
        {/* Subtle decorative background glow */}
        <div className="absolute top-1/4 -right-40 w-96 h-96 bg-orange-100/40 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-10 -left-40 w-96 h-96 bg-purple-100/40 rounded-full blur-3xl pointer-events-none"></div>

        <div className="container-custom px-4 sm:px-6 relative z-10">
          <div className="grid lg:grid-cols-12 gap-8 sm:gap-12 items-start">
            
            {/* Left Column: Direct Message Form (7 cols) */}
            <div className="lg:col-span-7 bg-white rounded-3xl p-6 sm:p-10 shadow-2xl border border-gray-100 relative">
              <div className="mb-6 sm:mb-8">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-orange-100/80 text-primary-700 text-xs font-bold uppercase tracking-wider mb-2">
                  <Sparkles className="w-3.5 h-3.5 text-primary-600" />
                  Direct Inquiry
                </span>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">
                  Send Us a Message
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Fill out the form below and our team will get back to you within 24–48 hours.
                </p>
              </div>

              {/* Quick Topic Chips */}
              <div className="mb-6">
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
                  Select Inquiry Topic
                </label>
                <div className="flex flex-wrap gap-2">
                  {INQUIRY_TOPICS.map((topic) => {
                    const isSelected = formData.subject === topic;
                    return (
                      <button
                        key={topic}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, subject: topic }))}
                        className={`text-xs px-3.5 py-1.5 rounded-full transition-all duration-200 font-medium ${
                          isSelected
                            ? 'bg-primary-600 text-white shadow-md shadow-primary-600/20 scale-105'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {topic}
                      </button>
                    );
                  })}
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                      <User className="w-3.5 h-3.5 text-primary-600" />
                      Your Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="name"
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="e.g. Gaurav Khandekar"
                      className="w-full px-3.5 py-2.5 text-sm bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all shadow-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                      <Phone className="w-3.5 h-3.5 text-primary-600" />
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="phone"
                      type="tel"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="e.g. (206) 555-0123"
                      className="w-full px-3.5 py-2.5 text-sm bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all shadow-sm"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                    <Mail className="w-3.5 h-3.5 text-primary-600" />
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="email"
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="e.g. you@example.com"
                    className="w-full px-3.5 py-2.5 text-sm bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all shadow-sm"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                    <Tag className="w-3.5 h-3.5 text-primary-600" />
                    Subject / Reason <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="subject"
                    type="text"
                    name="subject"
                    required
                    value={formData.subject}
                    onChange={handleInputChange}
                    placeholder="Brief summary of your inquiry"
                    className="w-full px-3.5 py-2.5 text-sm bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all shadow-sm"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-xs font-semibold text-gray-700 mb-1 flex items-center justify-between">
                    <span className="flex items-center gap-1.5">
                      <MessageSquare className="w-3.5 h-3.5 text-primary-600" />
                      Message <span className="text-red-500">*</span>
                    </span>
                    <span className="text-[11px] text-gray-400 font-normal">
                      {formData.message.length}/1000
                    </span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={4}
                    maxLength={1000}
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Tell us what you're looking for, event dates, questions, or collaboration details..."
                    className="w-full px-3.5 py-2.5 text-sm bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all shadow-sm resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 sm:py-3.5 rounded-xl bg-gradient-to-r from-primary-600 to-orange-600 hover:from-primary-700 hover:to-orange-700 text-white font-bold text-sm sm:text-base shadow-lg shadow-primary-500/25 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60 disabled:hover:scale-100 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Sending Your Message...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      <span>Send Message</span>
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Right Column: Department Hub & Trust Signals (5 cols) */}
            <div className="lg:col-span-5 space-y-6">
              <div>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-purple-100/80 text-purple-700 text-xs font-bold uppercase tracking-wider mb-2">
                  <Building className="w-3.5 h-3.5 text-purple-600" />
                  Specialized Teams
                </span>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900">
                  Contact by Department
                </h3>
                <p className="text-xs sm:text-sm text-gray-500 mt-1">
                  Have a specific inquiry? Reach out directly to the dedicated team lead.
                </p>
              </div>

              <div className="space-y-3 sm:space-y-3.5">
                {departments.map((dept, idx) => {
                  const DeptIcon = dept.icon;
                  return (
                    <div
                      key={idx}
                      className="bg-gray-50/80 hover:bg-white rounded-2xl p-4 sm:p-5 border border-gray-200/70 hover:border-primary-200 hover:shadow-lg transition-all duration-200 group"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-primary-100 text-primary-700 flex items-center justify-center shrink-0 group-hover:bg-primary-600 group-hover:text-white transition-colors duration-200">
                          <DeptIcon className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-bold text-gray-900 group-hover:text-primary-600 transition-colors">
                            {dept.name}
                          </h4>
                          <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">
                            {dept.description}
                          </p>
                          <a
                            href={`mailto:${dept.email}`}
                            className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary-600 hover:text-primary-800 mt-2"
                          >
                            <Mail className="w-3 h-3" />
                            <span>{dept.email}</span>
                          </a>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Trust Box */}
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-4 sm:p-5 border border-amber-200/70 flex items-start gap-3.5">
                <ShieldCheck className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" />
                <div className="text-xs leading-relaxed text-amber-900">
                  <p className="font-bold text-amber-950 mb-0.5">501(c)(3) Non-Profit Organization</p>
                  Beats of Washington is committed to cultural preservation, community music education, and youth empowerment. Your inquiries are handled directly by volunteer community organizers.
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Section 3: Interactive Map & Visiting Office */}
      <section className="py-12 sm:py-16 bg-gray-50 relative overflow-hidden border-t border-gray-100">
        <div className="container-custom px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto mb-8 sm:mb-12">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-bold uppercase tracking-wider mb-2">
              <MapPin className="w-3.5 h-3.5" />
              Community Presence
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900">
              Visit Us in Redmond & Greater Seattle
            </h2>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">
              We operate across Eastside King County with rehearsals, instrument storage, and community events throughout the Seattle metro area.
            </p>
          </div>

          <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-gray-200 bg-white">
            <iframe
              title="Beats of Washington Office Map"
              src="https://www.google.com/maps?q=9256+225th+Way+NE,+WA+98053&output=embed"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="w-full h-72 sm:h-96 border-0"
            ></iframe>

            {/* Glassmorphic floating card on bottom of map */}
            <div className="absolute bottom-4 left-4 right-4 sm:left-6 sm:right-auto sm:max-w-md bg-white/95 backdrop-blur-md p-4 sm:p-5 rounded-2xl shadow-xl border border-gray-200/80">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary-600 text-white flex items-center justify-center shrink-0">
                  <MapPin className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-bold text-gray-900">Beats of Washington</h4>
                  <p className="text-xs text-gray-600 mt-0.5">9256 225th Way NE, Redmond, WA 98053</p>
                  <a
                    href="https://www.google.com/maps/search/?api=1&query=9256+225th+Way+NE,+Redmond,+WA+98053"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-primary-600 hover:text-primary-700 mt-2"
                  >
                    <span>Open in Google Maps</span>
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: Social Media Strip */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="container-custom px-4 sm:px-6 text-center">
          <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">
            Stay Connected
          </p>
          <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6">
            Follow Beats of Washington on Social Media
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
            {socialLinks.map((social) => {
              const SocialIcon = social.icon;
              return (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-3 p-4 rounded-2xl border border-gray-200 hover:border-transparent bg-gray-50/60 hover:text-white transition-all duration-300 shadow-sm hover:shadow-lg hover:-translate-y-1 group"
                  style={{
                    transitionProperty: 'all'
                  }}
                  onMouseEnter={(e) => {
                    if (social.name === 'Facebook') e.currentTarget.style.backgroundColor = '#1877F2';
                    if (social.name === 'Instagram') e.currentTarget.style.backgroundColor = '#E1306C';
                    if (social.name === 'YouTube') e.currentTarget.style.backgroundColor = '#FF0000';
                    e.currentTarget.style.borderColor = 'transparent';
                    e.currentTarget.style.color = '#ffffff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '';
                    e.currentTarget.style.borderColor = '';
                    e.currentTarget.style.color = '';
                  }}
                >
                  <SocialIcon className="w-6 h-6 transition-transform group-hover:scale-110" />
                  <div className="text-left">
                    <p className="text-xs font-bold leading-tight">{social.name}</p>
                    <p className="text-[11px] opacity-80">{social.handle}</p>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section 5: Modern Interactive FAQ Accordion */}
      <section className="py-12 sm:py-20 bg-gray-50 relative">
        <div className="container-custom px-4 sm:px-6 max-w-4xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-bold uppercase tracking-wider mb-2">
              <Sparkles className="w-3.5 h-3.5" />
              Frequently Asked Questions
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900">
              Got Questions? We Have Answers.
            </h2>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">
              Find quick answers to common questions about volunteering, performances, and community membership.
            </p>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, idx) => {
              const isOpen = openFaq === idx;
              return (
                <div
                  key={idx}
                  className="bg-white rounded-2xl border border-gray-200/80 shadow-sm overflow-hidden transition-all duration-200"
                >
                  <button
                    type="button"
                    onClick={() => setOpenFaq(isOpen ? -1 : idx)}
                    className="w-full p-4 sm:p-5 text-left flex items-center justify-between gap-4 hover:bg-gray-50/50 transition-colors"
                    aria-expanded={isOpen}
                  >
                    <span className="text-sm sm:text-base font-bold text-gray-900">
                      {faq.question}
                    </span>
                    <ChevronDown
                      className={`w-5 h-5 text-gray-500 shrink-0 transition-transform duration-200 ${
                        isOpen ? 'rotate-180 text-primary-600' : ''
                      }`}
                    />
                  </button>

                  {isOpen && (
                    <div className="px-4 pb-4 sm:px-5 sm:pb-5 pt-0 text-xs sm:text-sm text-gray-600 leading-relaxed border-t border-gray-100 bg-gray-50/30">
                      {faq.answer}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-8 text-center">
            <p className="text-xs sm:text-sm text-gray-500">
              Still have a question?{' '}
              <a href="#name" className="text-primary-600 font-bold hover:underline">
                Send us a direct message above
              </a>
              {' '}or email{' '}
              <a href="mailto:beatsofredmond@gmail.com" className="text-primary-600 font-bold hover:underline">
                beatsofredmond@gmail.com
              </a>.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form Submission Success Popup Modal */}
      {showSuccessModal && (
        <div 
          className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[9999] p-3 sm:p-4 animate-fadeIn"
          onClick={(e) => e.target === e.currentTarget && setShowSuccessModal(false)}
        >
          <div className="bg-white rounded-3xl max-w-sm sm:max-w-md w-full p-6 sm:p-8 text-center shadow-2xl border border-orange-100 my-auto animate-scaleUp relative overflow-hidden">
            {/* Background festive glow */}
            <div className="absolute -top-16 -right-16 w-36 h-36 bg-orange-400/20 rounded-full blur-2xl pointer-events-none" />
            <div className="absolute -bottom-16 -left-16 w-36 h-36 bg-purple-500/20 rounded-full blur-2xl pointer-events-none" />

            {/* Celebration Badge */}
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-orange-100 text-orange-700 text-xs font-bold mb-4 animate-bounce">
              <Sparkles className="w-3.5 h-3.5 text-orange-500" />
              <span>Message Sent Successfully!</span>
            </div>

            {/* Spinning Logo Container with Glow Ring */}
            <div className="relative w-24 h-24 sm:w-28 sm:h-28 mx-auto mb-4 flex items-center justify-center">
              <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-primary-500 via-orange-400 to-yellow-400 animate-spin opacity-75 blur-sm" style={{ animationDuration: '4s' }} />
              <div className="relative w-full h-full rounded-full bg-white p-2 shadow-xl flex items-center justify-center border-2 border-orange-200 animate-logo-celebrate">
                {logoUrl ? (
                  <img 
                    src={logoUrl} 
                    alt="BOW Logo" 
                    className="w-full h-full object-contain rounded-full"
                  />
                ) : (
                  <div className="w-full h-full rounded-full bg-gradient-to-br from-primary-600 to-orange-500 flex items-center justify-center text-white text-2xl font-black">
                    BOW
                  </div>
                )}
              </div>
            </div>

            <h3 className="text-xl sm:text-2xl font-extrabold text-gray-900 mb-2">Thank You for Contacting Us!</h3>
            <p className="text-xs sm:text-sm text-gray-600 mb-6 leading-relaxed">
              Your message has been received by the Beats of Washington team. We will review your inquiry and get back to you shortly!
            </p>

            <button
              onClick={() => setShowSuccessModal(false)}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-primary-600 to-orange-600 hover:from-primary-700 hover:to-orange-700 text-white font-bold text-sm shadow-lg shadow-primary-500/25 transition-all duration-200 hover:scale-105"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ContactPage;